import React, { useState } from 'react';
import { X, ArrowRight, ArrowLeft, Check, Sparkles, Home, Hammer, Mountain, Waves, Wrench, Building2 } from 'lucide-react';
import API from '../../api';

const TIPI = [
  { id: 'Ristrutturazioni', label: 'Ristrutturazione', icon: Hammer, desc: 'Bagno, cucina, intero appartamento' },
  { id: 'Nuove Costruzioni', label: 'Nuova Costruzione', icon: Building2, desc: 'Villa, palazzina, edificio commerciale' },
  { id: 'Rifacimento Tetti', label: 'Tetto', icon: Mountain, desc: 'Rifacimento, isolamento, lattoneria' },
  { id: 'Realizzazione Piscine', label: 'Piscina', icon: Waves, desc: 'Piscina interrata in giardino' },
  { id: 'Manutenzioni', label: 'Manutenzioni', icon: Wrench, desc: 'Riparazioni, condomini, hotel' },
  { id: 'Altro', label: 'Altro', icon: Home, desc: 'Parlane con noi' },
];

const URGENZA = [
  { id: 'subito', label: 'Subito (entro 1 mese)', accent: true },
  { id: '1-3-mesi', label: 'Entro 3 mesi' },
  { id: '3-6-mesi', label: 'Entro 6 mesi' },
  { id: 'da-definire', label: 'Da definire' },
];

const MQ = [
  { id: '<50', label: 'Meno di 50 mq' },
  { id: '50-100', label: '50–100 mq' },
  { id: '100-200', label: '100–200 mq' },
  { id: '>200', label: 'Oltre 200 mq' },
  { id: 'na', label: 'Non applicabile' },
];

export default function QuickQuoteWidget({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    tipo_intervento: '', servizio: '', mq: '', urgenza: '',
    localita: '', nome: '', email: '', telefono: '', messaggio: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const next = () => setStep(s => Math.min(s + 1, 3));
  const back = () => setStep(s => Math.max(s - 1, 0));
  const reset = () => {
    setStep(0);
    setData({ tipo_intervento: '', servizio: '', mq: '', urgenza: '', localita: '', nome: '', email: '', telefono: '', messaggio: '' });
    setDone(false);
    setError('');
  };

  const canNext = () => {
    if (step === 0) return !!data.servizio;
    if (step === 1) return !!data.mq && !!data.urgenza;
    if (step === 2) return data.nome.trim().length >= 2 && data.email.includes('@');
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await API.post('/api/public/contact', {
        nome: data.nome,
        email: data.email,
        telefono: data.telefono,
        servizio: data.servizio,
        tipo_intervento: data.tipo_intervento || data.servizio,
        mq: data.mq,
        urgenza: data.urgenza,
        localita: data.localita,
        messaggio: data.messaggio || `Richiesta preventivo ${data.servizio} — ${data.mq} mq — urgenza ${data.urgenza}`,
        fonte: 'quick_quote_widget',
      });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore invio. Riprova o chiamaci direttamente.');
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="quick-quote-modal">
      <div className="bg-stone-950 text-white rounded-t-2xl sm:rounded-lg w-full sm:max-w-2xl max-h-[95vh] flex flex-col overflow-hidden border border-stone-800" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 sm:px-7 py-5 border-b border-stone-800 flex items-center justify-between bg-gradient-to-r from-primary/20 to-stone-950">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary text-white flex items-center justify-center">
              <Sparkles size={18} />
            </div>
            <div>
              <p className="text-xs font-black text-primary uppercase tracking-widest">Preventivo Gratuito</p>
              <p className="text-base sm:text-lg font-black">{done ? 'Richiesta inviata!' : `Step ${step + 1} di 3`}</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Chiudi" data-testid="quick-quote-close" className="p-2 hover:bg-white/10 rounded-md"><X size={20} /></button>
        </div>

        {/* Progress bar */}
        {!done && (
          <div className="h-1 bg-stone-800">
            <div className="h-full bg-primary transition-all duration-500" style={{ width: `${((step + 1) / 3) * 100}%` }} />
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-7">
          {done ? (
            <SuccessView nome={data.nome} onReset={reset} onClose={onClose} />
          ) : step === 0 ? (
            <StepTipo data={data} set={set} />
          ) : step === 1 ? (
            <StepDimensioni data={data} set={set} />
          ) : step === 2 ? (
            <StepContatti data={data} set={set} error={error} />
          ) : null}
        </div>

        {/* Footer */}
        {!done && (
          <div className="px-5 sm:px-7 py-4 border-t border-stone-800 flex items-center justify-between bg-stone-950 gap-3">
            {step > 0 ? (
              <button onClick={back} data-testid="quick-quote-back" className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold text-stone-300 hover:text-white hover:bg-white/5 rounded-md">
                <ArrowLeft size={14} /> Indietro
              </button>
            ) : <span />}
            {step < 2 ? (
              <button onClick={next} disabled={!canNext()} data-testid="quick-quote-next"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white font-black text-sm rounded-md hover:bg-primary disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-primary/20">
                Avanti <ArrowRight size={14} />
              </button>
            ) : (
              <button onClick={submit} disabled={!canNext() || submitting} data-testid="quick-quote-submit"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white font-black text-sm rounded-md hover:bg-primary disabled:opacity-40 transition-all shadow-lg shadow-primary/20">
                {submitting ? 'Invio...' : <>Invia richiesta <Check size={14} /></>}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StepTipo({ data, set }) {
  return (
    <div>
      <h3 className="text-xl sm:text-2xl font-black mb-1">Che tipo di lavoro hai in mente?</h3>
      <p className="text-stone-400 text-sm mb-6">Seleziona la categoria più vicina al tuo progetto.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {TIPI.map(t => {
          const active = data.servizio === t.id;
          return (
            <button key={t.id} onClick={() => set('servizio', t.id)} data-testid={`qq-tipo-${t.id}`}
              className={`text-left p-4 rounded-lg border-2 transition-all ${active ? 'border-primary bg-primary/10' : 'border-stone-800 hover:border-stone-600 bg-stone-900/50'}`}>
              <div className="flex items-center gap-3 mb-1">
                <t.icon size={20} className={active ? 'text-primary' : 'text-stone-400'} />
                <p className={`font-bold ${active ? 'text-white' : 'text-stone-200'}`}>{t.label}</p>
              </div>
              <p className="text-xs text-stone-400">{t.desc}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepDimensioni({ data, set }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl sm:text-2xl font-black mb-1">Dimensioni e tempistiche</h3>
        <p className="text-stone-400 text-sm mb-4">Servono solo per stimare correttamente.</p>
      </div>
      <div>
        <label className="block text-[11px] font-black text-primary uppercase tracking-widest mb-2">Metratura indicativa</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {MQ.map(m => (
            <button key={m.id} onClick={() => set('mq', m.id)} data-testid={`qq-mq-${m.id}`}
              className={`px-3 py-2.5 rounded-md text-sm font-semibold transition-all border ${data.mq === m.id ? 'bg-primary text-white border-primary' : 'bg-stone-900 border-stone-800 text-stone-300 hover:border-stone-600'}`}>
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-[11px] font-black text-primary uppercase tracking-widest mb-2">Quando vorresti iniziare?</label>
        <div className="grid grid-cols-2 gap-2">
          {URGENZA.map(u => (
            <button key={u.id} onClick={() => set('urgenza', u.id)} data-testid={`qq-urg-${u.id}`}
              className={`px-3 py-2.5 rounded-md text-sm font-semibold transition-all border ${data.urgenza === u.id ? 'bg-primary text-white border-primary' : 'bg-stone-900 border-stone-800 text-stone-300 hover:border-stone-600'} ${u.accent && data.urgenza !== u.id ? 'ring-1 ring-primary/40' : ''}`}>
              {u.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-[11px] font-black text-primary uppercase tracking-widest mb-2">Località cantiere (opzionale)</label>
        <input value={data.localita} onChange={e => set('localita', e.target.value)} data-testid="qq-localita"
          placeholder="Es: Calcinato, Brescia, Sirmione..."
          className="w-full px-3 py-2.5 bg-stone-900 border border-stone-800 rounded-md text-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none" />
      </div>
    </div>
  );
}

function StepContatti({ data, set, error }) {
  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-xl sm:text-2xl font-black mb-1">Lasciaci i tuoi dati</h3>
        <p className="text-stone-400 text-sm">Ti ricontatteremo entro 24 ore lavorative con un preventivo personalizzato.</p>
      </div>
      <div className="bg-primary/10 border border-primary/30 rounded-md p-3 text-xs text-primary">
        <strong>Riepilogo:</strong> {data.servizio || '—'} · {data.mq || '—'} · {data.urgenza || '—'}{data.localita ? ` · ${data.localita}` : ''}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Nome e cognome *" value={data.nome} onChange={v => set('nome', v)} testid="qq-nome" />
        <Field label="Telefono" value={data.telefono} onChange={v => set('telefono', v)} testid="qq-tel" type="tel" />
      </div>
      <Field label="Email *" value={data.email} onChange={v => set('email', v)} testid="qq-email" type="email" />
      <div>
        <label className="block text-[11px] font-black text-primary uppercase tracking-widest mb-1.5">Dettagli aggiuntivi (opzionale)</label>
        <textarea rows={3} value={data.messaggio} onChange={e => set('messaggio', e.target.value)} data-testid="qq-msg"
          placeholder="Es: ristrutturazione bagno con sostituzione sanitari, rivestimenti gres..."
          className="w-full px-3 py-2.5 bg-stone-900 border border-stone-800 rounded-md text-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-none" />
      </div>
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-3 rounded-md text-sm font-semibold" data-testid="qq-error">{error}</div>
      )}
      <p className="text-[11px] text-stone-500">Trattamento dati ai sensi del GDPR. Non condivideremo i tuoi dati con terzi.</p>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = 'text' }) {
  return (
    <div>
      <label className="block text-[11px] font-black text-primary uppercase tracking-widest mb-1.5">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="w-full px-3 py-2.5 bg-stone-900 border border-stone-800 rounded-md text-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none" />
    </div>
  );
}

function SuccessView({ nome, onReset, onClose }) {
  return (
    <div className="text-center py-6" data-testid="qq-success">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 border-2 border-green-500/50 flex items-center justify-center">
        <Check size={32} className="text-green-400" />
      </div>
      <h3 className="text-2xl sm:text-3xl font-black mb-2">Grazie {nome.split(' ')[0]}!</h3>
      <p className="text-stone-300 max-w-md mx-auto mb-6">
        La tua richiesta è stata inviata. Ti contatteremo entro <strong className="text-white">24 ore lavorative</strong> con un preventivo personalizzato.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button onClick={onClose} className="px-5 py-2.5 bg-primary text-white font-bold rounded-md hover:bg-primary">
          Chiudi
        </button>
        <button onClick={onReset} className="px-5 py-2.5 border border-stone-700 text-stone-300 font-bold rounded-md hover:bg-white/5">
          Invia un'altra richiesta
        </button>
      </div>
    </div>
  );
}
